import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { toast } from 'components/ui/toast-store'
import { cn } from 'lib/utils'
import { ImagePlus, Upload } from 'lucide-react'
import {
	uploadToCloudinary,
	normalizeCloudinaryResult,
} from 'components/FileUploader/fileUploadUtils'
import NotePlanStep from './NotePlanStep'
import NoteComposeStep from './NoteComposeStep'
import StagedImageGrid from './StagedImageGrid'
import UploadProgressStep from './UploadProgressStep'
import { createCommitState } from './commitImageUpload'
import {
	MAX_FILES,
	type CommitImage,
	type ImageUploadWizardProps,
	type CommitState,
	type NotePlan,
	type StagedImage,
} from './imageUploadWizard.types'

type Step = 'select' | 'choose' | 'compose' | 'upload'
type Phase = 'idle' | 'uploading' | 'committing' | 'commit-error' | 'done'

/** Cloudinary uploads run a few at a time, matching the document library. */
const UPLOAD_CONCURRENCY = 3

const makeId = () => Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)

const toStaged = (file: File): StagedImage => ({
	id: makeId(),
	file,
	preview: URL.createObjectURL(file),
	noteHtml: '',
	status: 'pending',
})

/**
 * Upload one or more images into a project, portfolio or site, capturing the
 * note that goes with them in the same pass.
 *
 * Nothing leaves the browser until Confirm. That is a deliberate change from
 * MultiFileUploader, which uploaded on drop: it means a mis-picked file can be
 * removed, an abandoned dialog leaves no orphaned Cloudinary assets, and the
 * note can be written while looking at the images it describes.
 *
 * The component is entity-agnostic. Everything specific to a project,
 * portfolio or site lives behind `target.onCommit`.
 */
const ImageUploadWizard: React.FC<ImageUploadWizardProps> = ({
	open,
	onClose,
	target,
	initialFiles,
	maxFiles = MAX_FILES,
}) => {
	const [step, setStep] = useState<Step>('select')
	const [staged, setStaged] = useState<StagedImage[]>([])
	const [plan, setPlan] = useState<NotePlan | null>(null)
	const [sharedNoteHtml, setSharedNoteHtml] = useState('')
	const [phase, setPhase] = useState<Phase>('idle')
	const [commitError, setCommitError] = useState<string | null>(null)

	// Survives across retries so a re-run skips what already landed.
	const commitStateRef = useRef<CommitState>(createCommitState())
	// Read by the unmount cleanup, which must not re-run on every staged change.
	const stagedRef = useRef<StagedImage[]>([])
	stagedRef.current = staged

	const revokeAll = useCallback((images: StagedImage[]) => {
		images.forEach(image => URL.revokeObjectURL(image.preview))
	}, [])

	const resetState = useCallback(() => {
		revokeAll(stagedRef.current)
		setStaged([])
		setPlan(null)
		setSharedNoteHtml('')
		setStep('select')
		setPhase('idle')
		setCommitError(null)
		commitStateRef.current = createCommitState()
	}, [revokeAll])

	// Revoke previews on unmount. Deliberately not depending on `staged` —
	// stagedRef always holds the latest list.
	useEffect(
		() => () => {
			revokeAll(stagedRef.current)
		},
		[revokeAll],
	)

	// A caller that ran its own file picker can hand the files straight in.
	useEffect(() => {
		if (!open || !initialFiles?.length) return
		setStaged(prev => {
			if (prev.length > 0) return prev
			return initialFiles.slice(0, maxFiles).map(toStaged)
		})
		setStep('choose')
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const addFiles = useCallback(
		(files: File[]) => {
			setStaged(prev => {
				const room = maxFiles - prev.length
				if (room <= 0) {
					toast.warning('You can upload up to ' + maxFiles + ' images at a time.')
					return prev
				}
				if (files.length > room) {
					toast.warning('Only the first ' + room + ' images were added (limit ' + maxFiles + ').')
				}
				return [...prev, ...files.slice(0, room).map(toStaged)]
			})
		},
		[maxFiles],
	)

	const removeFile = useCallback((id: string) => {
		setStaged(prev => {
			const target = prev.find(i => i.id === id)
			if (target) URL.revokeObjectURL(target.preview)
			return prev.filter(i => i.id !== id)
		})
	}, [])

	const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
		noClick: true,
		noKeyboard: true,
		multiple: true,
		accept: { 'image/*': [] },
		onDropAccepted: addFiles,
		onDropRejected: () => toast.error('Only image files can be uploaded here.'),
	})

	const setImageNote = useCallback((id: string, html: string) => {
		setStaged(prev => prev.map(i => (i.id === id ? { ...i, noteHtml: html } : i)))
	}, [])

	const patch = useCallback((id: string, changes: Partial<StagedImage>) => {
		setStaged(prev => prev.map(i => (i.id === id ? { ...i, ...changes } : i)))
	}, [])

	/**
	 * Upload the given images to Cloudinary, a few at a time, recording each
	 * outcome on its row. Returns the images that succeeded.
	 */
	const runUploads = useCallback(
		async (toUpload: StagedImage[]): Promise<StagedImage[]> => {
			const results = new Map<string, StagedImage>()
			const queue = [...toUpload]

			const worker = async () => {
				for (;;) {
					const image = queue.shift()
					if (!image) return

					patch(image.id, { status: 'uploading', errorMessage: undefined })
					try {
						const upload = await uploadToCloudinary(
							image.file,
							target.folder ? { folder: target.folder } : undefined,
						)
						patch(image.id, { status: 'uploaded', upload })
						results.set(image.id, { ...image, status: 'uploaded', upload })
					} catch (error) {
						const message = error instanceof Error ? error.message : 'Upload failed'
						patch(image.id, { status: 'error', errorMessage: message })
					}
				}
			}

			await Promise.all(
				Array.from({ length: Math.min(UPLOAD_CONCURRENCY, toUpload.length) }, worker),
			)

			return toUpload.map(i => results.get(i.id)).filter(Boolean) as StagedImage[]
		},
		[patch, target.folder],
	)

	const toCommitImages = (images: StagedImage[]): CommitImage[] =>
		images
			.filter(i => i.upload)
			.map(i => ({
				id: i.id,
				file: i.file,
				upload: i.upload!,
				normalized: normalizeCloudinaryResult(i.upload!, 'drag-and-drop'),
				noteHtml: i.noteHtml,
			}))

	const runCommit = useCallback(
		async (images: StagedImage[]) => {
			setPhase('committing')
			setCommitError(null)
			try {
				await target.onCommit(
					{
						plan: plan ?? 'none',
						images: toCommitImages(images),
						sharedNoteHtml,
					},
					// Carries what a failed attempt already wrote, so Retry save
					// does not duplicate a note or a gallery row.
					commitStateRef.current,
				)
				setPhase('done')
				toast.success(
					images.length === 1 ? 'Image uploaded.' : images.length + ' images uploaded.',
				)
				resetState()
				onClose()
			} catch (error) {
				const message = error instanceof Error ? error.message : 'Could not save'
				setPhase('commit-error')
				setCommitError(message)
			}
		},
		[plan, sharedNoteHtml, target, resetState, onClose],
	)

	const handleConfirm = useCallback(async () => {
		setStep('upload')
		setPhase('uploading')

		const uploaded = await runUploads(staged)

		if (uploaded.length === 0) {
			setPhase('idle')
			toast.error('None of the images could be uploaded. Please try again.')
			return
		}

		// Some failed. Stop and let the user decide rather than silently
		// dropping images they believe they uploaded.
		if (uploaded.length < staged.length) {
			setPhase('idle')
			return
		}

		await runCommit(uploaded)
	}, [runUploads, runCommit, staged])

	const handleRetryFailed = useCallback(async () => {
		const failed = staged.filter(i => i.status === 'error')
		if (failed.length === 0) return

		setPhase('uploading')
		await runUploads(failed)
		setPhase('idle')
	}, [runUploads, staged])

	const handleContinuePartial = useCallback(async () => {
		const uploaded = staged.filter(i => i.status === 'uploaded')
		const skipped = staged.length - uploaded.length

		const ok = await toast.confirm({
			title: 'Continue without every image?',
			message:
				skipped +
				' of ' +
				staged.length +
				' images could not be uploaded and will be left out.',
			confirmLabel: 'Continue with ' + uploaded.length,
			variant: 'warning',
		})
		if (!ok) return

		await runCommit(uploaded)
	}, [runCommit, staged])

	const handleClose = useCallback(async () => {
		// Nothing uploaded yet means the picked files would simply be lost.
		if (staged.length > 0 && phase === 'idle' && step !== 'upload') {
			const ok = await toast.confirm({
				title: 'Discard selected images?',
				message:
					staged.length === 1
						? 'This image has not been uploaded yet.'
						: 'These ' + staged.length + ' images have not been uploaded yet.',
				confirmLabel: 'Discard',
			})
			if (!ok) return
		}

		// A partial upload means real Cloudinary assets exist with no rows
		// pointing at them. Say so rather than losing it silently.
		if (phase === 'commit-error') {
			const ok = await toast.confirm({
				title: 'Close without finishing?',
				message: 'Some images or notes were not saved. Closing will leave them out.',
				confirmLabel: 'Close anyway',
			})
			if (!ok) return
		}

		resetState()
		onClose()
	}, [staged.length, phase, step, resetState, onClose])

	const uploadedCount = staged.filter(i => i.status === 'uploaded').length
	const failedCount = staged.filter(i => i.status === 'error').length
	const hasPartialFailure = step === 'upload' && phase === 'idle' && failedCount > 0

	const title = useMemo(() => {
		if (step === 'upload') return 'Uploading images'
		if (step === 'compose') return plan === 'single' ? 'Add a note' : 'Add notes'
		if (step === 'choose') return 'Add a note?'
		return 'Upload images'
	}, [step, plan])

	const busy = phase === 'uploading' || phase === 'committing'

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogContent className="max-w-2xl p-0">
				<DialogHeader className="px-4 pb-0 pt-4">
					<div className="flex items-center gap-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
							<ImagePlus className="h-5 w-5" />
						</div>
						<div className="min-w-0">
							<DialogTitle>{title}</DialogTitle>
							<DialogDescription className="truncate">
								{target.entityLabel}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="max-h-[60vh] overflow-y-auto p-4">
					{step === 'select' && (
						<div className="space-y-3">
							<div
								{...getRootProps()}
								className={cn(
									'flex flex-col items-center justify-center rounded-lg border border-dashed px-4 py-8 text-center transition-colors',
									isDragActive
										? 'border-primary bg-primary/5'
										: 'border-border bg-muted/40',
								)}
							>
								<input {...getInputProps()} />
								<Upload className="mb-2 h-6 w-6 text-muted-foreground" />
								<p className="text-sm text-foreground">Drag and drop images here</p>
								<p className="mb-3 text-xs text-muted-foreground">
									Up to {maxFiles} at a time
								</p>
								<Button type="button" variant="primary" onClick={openFileDialog}>
									Browse device
								</Button>
							</div>

							{staged.length > 0 && (
								<>
									<p className="text-xs text-muted-foreground">
										{staged.length} selected. Nothing is uploaded until you finish.
									</p>
									<StagedImageGrid images={staged} onRemove={removeFile} />
								</>
							)}
						</div>
					)}

					{step === 'choose' && (
						<div className="space-y-4">
							<NotePlanStep
								imageCount={staged.length}
								value={plan}
								onChange={setPlan}
							/>
							<StagedImageGrid images={staged} onRemove={removeFile} />
						</div>
					)}

					{step === 'compose' && plan && plan !== 'none' && (
						<NoteComposeStep
							plan={plan}
							images={staged}
							sharedNoteHtml={sharedNoteHtml}
							onSharedNoteChange={setSharedNoteHtml}
							onImageNoteChange={setImageNote}
						/>
					)}

					{step === 'upload' && (
						<div className="space-y-3">
							<UploadProgressStep
								images={staged}
								isCommitting={phase === 'committing'}
							/>
							{commitError && (
								<p className="text-sm text-destructive">
									Could not save everything: {commitError}
								</p>
							)}
						</div>
					)}
				</div>

				<div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
					<Button type="button" variant="ghost" onClick={handleClose} disabled={busy}>
						{step === 'upload' && phase === 'done' ? 'Close' : 'Cancel'}
					</Button>

					<div className="flex items-center gap-2">
						{step === 'choose' && (
							<Button
								type="button"
								variant="ghost"
								onClick={() => setStep('select')}
							>
								Back
							</Button>
						)}
						{step === 'compose' && (
							<Button
								type="button"
								variant="ghost"
								onClick={() => setStep('choose')}
							>
								Back
							</Button>
						)}

						{step === 'select' && (
							<Button
								type="button"
								variant="primary"
								disabled={staged.length === 0}
								onClick={() => setStep('choose')}
							>
								Next
							</Button>
						)}

						{step === 'choose' && (
							<Button
								type="button"
								variant="primary"
								disabled={plan === null}
								onClick={() => (plan === 'none' ? handleConfirm() : setStep('compose'))}
							>
								{plan === 'none' ? 'Upload' : 'Next'}
							</Button>
						)}

						{step === 'compose' && (
							<Button type="button" variant="primary" onClick={handleConfirm}>
								Upload and save
							</Button>
						)}

						{hasPartialFailure && (
							<>
								<Button type="button" variant="ghost" onClick={handleRetryFailed}>
									Retry failed
								</Button>
								{uploadedCount > 0 && (
									<Button
										type="button"
										variant="primary"
										onClick={handleContinuePartial}
									>
										Continue with {uploadedCount}
									</Button>
								)}
							</>
						)}

						{phase === 'commit-error' && (
							<Button
								type="button"
								variant="primary"
								onClick={() => runCommit(staged.filter(i => i.status === 'uploaded'))}
							>
								Retry save
							</Button>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export default ImageUploadWizard
